/**
 * Bridge the gap between a bulk on/off HTTP write landing and the live
 * switches actually flipping.
 *
 * The opener/unwinder per-row enabled switch is driven by the WS envelope
 * (`pairEnabled` / `unwinderEnabled` in the live store), NOT by the bulk
 * PATCH response. That PATCH is a single Mongo `updateMany` that returns in
 * ~100ms, but the engine only re-publishes the new state a beat later
 * (change-stream debounce ~0.5-2s). If the spinner releases at PATCH-return,
 * the operator sees it flash-then-vanish while the switches sit on their old
 * value — reads as "nothing happened / not working".
 *
 * `waitForLiveSettle` keeps the bulk action busy until every affected id
 * reports the target state in the live store, so the loader honestly spans
 * the whole effect.
 *
 * The timeout is a UX CAP, not a correctness mechanism — the server write has
 * already succeeded before we start waiting; a dropped/late WS frame must
 * never wedge the spinner ON forever. Rows the store holds no envelope for
 * are handled by the caller's predicate (return `true` for `undefined`) so
 * they never block the settle.
 */
import type { LiveStore } from './createLiveStore';

export interface WaitForLiveSettleOptions {
  /** Hard cap before resolving regardless of state. Default 8000ms. */
  timeoutMs?: number;
  /** Poll cadence. Default 120ms. */
  intervalMs?: number;
}

export function waitForLiveSettle<T>(
  store: LiveStore<T>,
  ids: string[],
  predicate: (state: T | undefined) => boolean,
  options: WaitForLiveSettleOptions = {}
): Promise<void> {
  const { timeoutMs = 8000, intervalMs = 120 } = options;
  if (ids.length === 0) return Promise.resolve();

  const settled = () => ids.every(id => predicate(store.getKeySnapshot(id)));
  if (settled()) return Promise.resolve();

  return new Promise<void>(resolve => {
    const start = Date.now();
    const handle = setInterval(() => {
      if (settled() || Date.now() - start >= timeoutMs) {
        clearInterval(handle);
        resolve();
      }
    }, intervalMs);
  });
}
